import { type SyntheticEvent, useEffect, useState } from "react";

import { Alert, Snackbar } from "@mui/material";

import { announcementsAtom } from "@/src/state/announcementAtom";

import { useAuth } from "@/src/auth/hooks/useAuth";
import { Announcement, UserRole } from "@/src/types/types";

import { useAtom } from "jotai";

const AnnouncementContainer = () => {
  const [open, setOpen] = useState(false);
  const [announcements] = useAtom<Announcement[]>(announcementsAtom);
  const [visibleAnnouncement, setVisibleAnnouncement] =
    useState<Announcement | null>(null);
  const auth = useAuth();
  const [announcementTimer, setAnnouncementTimer] = useState(0);
  const [alreadyDisplayed, setAlreadyDisplayed] = useState(false);

  const checkAndUpdateAnnouncement = () => {
    const announcementDisabled =
      localStorage.getItem("announcementDisabled") === "true";
    const currentTime = new Date();

    const sortedAnnouncements = [...announcements].sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );

    if (sortedAnnouncements.length > 0) {
      const newestAnnouncement = sortedAnnouncements[0];
      if (new Date(newestAnnouncement.endDate) < currentTime) {
        localStorage.removeItem("announcementDisabled");
      }
    }

    const filteredAnnouncement = announcements.find((announcement) => {
      const { startDate, endDate, audience } = announcement;
      const isMatchingDate =
        new Date(startDate) < currentTime && currentTime < new Date(endDate);

      const isMatchingAudience =
        !audience ||
        (audience === UserRole.Consumer && auth.isConsumer) ||
        (audience === UserRole.Dealer && auth.isDealer) ||
        (audience === UserRole.Admin && auth.isAdmin);

      return isMatchingDate && isMatchingAudience && !announcementDisabled;
    });

    if (filteredAnnouncement && !alreadyDisplayed) {
      setVisibleAnnouncement(filteredAnnouncement);
      setOpen(true);
    } else if (!filteredAnnouncement) {
      setVisibleAnnouncement(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const counter = announcementTimer + 1;
      setAnnouncementTimer(counter);
      checkAndUpdateAnnouncement();
    }, 5 * 1000);
    return () => clearInterval(timer);
  }, [announcements, auth, announcementTimer]);

  const handleClose = (event?: SyntheticEvent | Event, reason?: string) => {
    if (reason !== "clickaway") {
      setOpen(false);
      setAlreadyDisplayed(true);
      localStorage.setItem("announcementDisabled", "true");
    }
  };

  return (
    <>
      {visibleAnnouncement && (
        <Snackbar
          id="announcementSnackbar"
          open={open}
          onClose={handleClose}
          disableWindowBlurListener={true}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center"
          }}
          sx={{
            maxWidth: ["100%", "calc(100% - 48px)"],
            top: ["4rem !important", "1.5rem !important"],
            right: ["0.5rem !important", "1rem !important"],
            ".MuiPaper-root": {
              width: "100%",
              ".MuiAlert-action": {
                padding: "0.1rem 0 0 0.5rem"
              }
            }
          }}
        >
          <Alert
            variant="filled"
            severity={visibleAnnouncement.severity}
            onClose={handleClose}
          >
            {visibleAnnouncement.content}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AnnouncementContainer;
